/** @format */

import React, { useCallback, useEffect, useState } from 'react';
import { Checkbox, makeStyles, Table, TableBody, TableCell, TableFooter, TableHead, TableRow } from '@material-ui/core';
import I18n from '../../../components/Translate/I18n';
import { FormGeneralHelper } from '../../../helpers/FormGeneral';
import { ItemLoaders } from '../../../core/loaders/ItemLoaders';
import { configDefault } from '../../../core/config';
import { useDispatch, useSelector } from 'react-redux';
import Filter from '../../../interfaces/forms/Filter';
import adescomStyle from '../../../assets/jss/adescom/adescomStyle';
import Pager from '../../../components/Adescom/Pager';
import { fractionsConstants } from '../../../modules/fractions/actions';
import AddButton from '../../../components/Buttons/AddButton';
import { fractionServiceCodeConstants } from '../../../modules/servicecode/reducers';
import oplAppStyle from '../../../assets/themes/OPL/jss/App/OplAppStyle';
import { withStyles } from '@material-ui/core/styles';
import TableHeaderContentCell from '../../../components/Table/TableHeaderContentCell';

interface FractionListAvaibleListInterface {
    classes: any;
    serviceCodeId: number;
}

const FractionListAvaibleList = (props: FractionListAvaibleListInterface): React.ReactElement => {
    const dispatch = useDispatch();

    const [filters, setFilters] = useState<Array<Filter>>([{ name: 'noServiceCode', value: props.serviceCodeId }]);
    const [checkedFractionServiceCodes, setCheckedFractionServiceCodes] = useState<Array<number>>([]);
    const [isCheckedFractionsAll, setIsCheckedFractionsAll] = useState<boolean>(false);

    const tableCellRoot = {
        root: props.classes.tableCellRootBordered,
    };
    const tableStickyHeader = {
        root: props.classes.tableStickyHeader,
    };

    const { collections } = useSelector((state: { fractionsReducers: any }) => {
        return state.fractionsReducers;
    });

    const getFractions = useCallback((): void => {
        const itemLoaders = new ItemLoaders(dispatch);
        itemLoaders.getListWithCallback(
            FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/fractions', configDefault.PLATFORM_API_PREFIX)),
            fractionsConstants.LIST,
            [{ name: 'noServiceCode', value: props.serviceCodeId }],
            [],
            (data: any) => {
                dispatch({
                    type: fractionsConstants.LIST,
                    collections: data,
                });
            },
            {
                propagateDataToCallback: true,
                dispatchAfterCallback: false,
            }
        );
    }, [dispatch, props.serviceCodeId]);
    const handleFractionCheck = (event: React.ChangeEvent<HTMLInputElement>): void => {
        let id = parseInt(event.target.name);
        let newCheckedFractionServiceCodesArr = [];
        if (checkedFractionServiceCodes.includes(id)) {
            newCheckedFractionServiceCodesArr = checkedFractionServiceCodes.filter((item: number) => item !== id);
        } else {
            newCheckedFractionServiceCodesArr = [...checkedFractionServiceCodes, id];
        }
        setCheckedFractionServiceCodes(newCheckedFractionServiceCodesArr);
    };

    const handleAddCheckedElements = (): void => {
        const itemLoaders = new ItemLoaders(dispatch);
        const isOneElement = checkedFractionServiceCodes.length === 1;
        itemLoaders.postWithCallback(
            FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/fraction_service_codes/multiple', configDefault.PLATFORM_API_PREFIX)),
            fractionServiceCodeConstants.FRACTION_SERVICE_CODE_ADD,
            {
                serviceCodeId: props.serviceCodeId,
                fractionId: isOneElement ? checkedFractionServiceCodes[0] : -1,
                fractionIdCollection: checkedFractionServiceCodes.join(','),
            },
            (data: any) => {
                setCheckedFractionServiceCodes([]);
                setIsCheckedFractionsAll(false);
                getFractions();
                const itemLoaders = new ItemLoaders(dispatch);
                itemLoaders.getListWithCallback(
                    FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/fraction_service_codes', configDefault.PLATFORM_API_PREFIX)),
                    fractionServiceCodeConstants.FRACTION_SERVICE_CODE_LIST,
                    [
                        {
                            name: 'serviceCodeId',
                            value: props.serviceCodeId,
                        },
                    ],
                    [],
                    (data: any) => {
                        dispatch({
                            type: fractionServiceCodeConstants.FRACTION_SERVICE_CODE_LIST,
                            collections: data,
                        });
                    },
                    {
                        propagateDataToCallback: true,
                        dispatchAfterCallback: false,
                    }
                );
            },
            {
                propagateDataToCallback: true,
                dispatchAfterCallback: false,
            }
        );
    };

    const paginationCallbackFunction = (uri: string | null = null): void => {
        if (!uri) {
            uri = FormGeneralHelper.getPath('/fractions', configDefault.PLATFORM_API_PREFIX);
        }
        const itemLoaders = new ItemLoaders(dispatch);
        itemLoaders.getListWithCallback(
            FormGeneralHelper.getByUri(uri),
            fractionsConstants.LIST,
            [{ name: 'noServiceCode', value: props.serviceCodeId }],
            [],
            (data: any) => {
                dispatch({
                    type: fractionsConstants.LIST,
                    collections: data,
                });
            },
            {
                propagateDataToCallback: true,
                dispatchAfterCallback: false,
            }
        );
    };

    const handleCheckAll = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const newCheckedFractions: Array<number> = [];
        if (!isCheckedFractionsAll) {
            collections &&
                collections['hydra:member'].forEach((el: any) => {
                    newCheckedFractions.push(el.id);
                });
            setCheckedFractionServiceCodes(newCheckedFractions);
            setIsCheckedFractionsAll(true);
        } else {
            setCheckedFractionServiceCodes([]);
            setIsCheckedFractionsAll(false);
        }
    };

    useEffect(() => {
        getFractions();
    }, [getFractions]);

    return (
        <Table>
            <TableHead>
                <TableRow>
                    <TableCell classes={tableStickyHeader}>
                        <Checkbox checked={isCheckedFractionsAll} onChange={handleCheckAll} />
                        <AddButton
                            nonTableButton
                            disabled={checkedFractionServiceCodes.length === 0}
                            callback={handleAddCheckedElements}
                            tooltipTitle="service_code:b.add_fraction"
                            tooltipPlacement="bottom-start"
                        />
                    </TableCell>
                    <TableCell classes={tableStickyHeader}>
                        <TableHeaderContentCell textAlign="left" title="service_code:h.fractions_avaible" showSort={false} onlyFilter={false} />
                    </TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {collections &&
                    collections['hydra:member'].map((el: any) => {
                        return (
                            <TableRow key={el.id}>
                                <TableCell classes={tableCellRoot}>
                                    <Checkbox checked={checkedFractionServiceCodes.includes(el.id)} name={el.id} onChange={handleFractionCheck} />
                                </TableCell>
                                <TableCell classes={tableCellRoot}>{el.mainname}</TableCell>
                            </TableRow>
                        );
                    })}
            </TableBody>
            <TableFooter>
                <TableRow>
                    <TableCell colSpan={2}>
                        {collections && collections['hydra:view'] && (
                            <Pager callback={paginationCallbackFunction} views={collections['hydra:view']} />
                        )}
                    </TableCell>
                </TableRow>
            </TableFooter>
        </Table>
    );
};

export default withStyles(oplAppStyle)(FractionListAvaibleList);
